import { useState, useEffect, useRef } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai";
import ReactMarkdown from "react-markdown";
import { 
  Loader, 
  X, 
  Send, 
  RefreshCw, 
  Copy, 
  Download, 
  Share2,
  Volume2,
  Mic,
  StopCircle,
  CheckCircle,
  Sparkles
} from "lucide-react";
import html2pdf from 'html2pdf.js';

const LOADING_MESSAGES = [
  "Analyzing data patterns...",
  "Processing insights...",
  "Generating comprehensive analysis...",
  "Evaluating key factors...",
  "Preparing detailed response...",
  "Synthesizing information..."
];
const SUGGESTIONS = [
  {
    text: "Could you elaborate on that?",
    icon: "✨",
    prompt: "Please provide more detailed information about your previous response, including specific examples and deeper insights."
  },
  {
    text: "Summarize key points",
    icon: "📝",
    prompt: "Please summarize the main points from your previous response in a clear, concise way."
  },
  {
    text: "Practical applications",
    icon: "💡",
    prompt: "What are the practical applications or real-world implications of what you just explained?"
  },
  {
    text: "Any limitations?",
    icon: "🤔",
    prompt: "What are the potential limitations or challenges we should consider in this analysis?"
  },
  {
    text: "Alternative perspectives",
    icon: "🔄",
    prompt: "Could you provide alternative perspectives or approaches to this analysis?"
  },
  {
    text: "Simple explanation",
    icon: "🎯",
    prompt: "Could you explain this in simpler terms, as if you're explaining to someone new to the topic?"
  }
];
// Helper function to remove markdown syntax
const removeMarkdown = (text) => {
  return text
    .replace(/#{1,6}\s/g, '') // Remove headers
    .replace(/\*\*/g, '') // Remove bold
    .replace(/\*/g, '') // Remove italic
    .replace(/`{3}[\s\S]*?`{3}/g, 'code block') // Replace code blocks
    .replace(/`([^`]+)`/g, '$1') // Remove inline code
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1') // Replace links with just text
    .replace(/\n+/g, ' ') // Replace multiple newlines with space
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
};

const GEMINI_API_KEY = 'AIzaSyAAfGQv9d4KCBhcdBvmukfW1GajtbSIRP0';
const GeminiAnalysis = ({ 
  onClose, 
  initialPrompt, 
  data,
  title = "AI Analysis",
  placeholder = "Ask a follow-up question..."
}) => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [input, setInput] = useState("");
    const [loadingMessage, setLoadingMessage] = useState("");
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [activeSuggestions, setActiveSuggestions] = useState(SUGGESTIONS);
    const messagesEndRef = useRef(null);
    const contentRef = useRef(null);
    const speechRef = useRef(null);
  
    // Cleanup function for speech synthesis
    useEffect(() => {
      return () => {
        if (window.speechSynthesis) {
          window.speechSynthesis.cancel();
        }
      };
    }, []);
  
    // Handle page visibility change
    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.hidden && window.speechSynthesis) {
          window.speechSynthesis.cancel();
          setIsSpeaking(false);
        }
      };
  
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }, []);

  useEffect(() => {
    if (initialPrompt && data) {
      generateAnalysis();
    }
  }, []);

  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setLoadingMessage(LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isLoading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Speech Recognition Setup
  const startListening = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      
      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
      };
      
      recognition.start();
    } else {
      alert("Speech recognition is not supported in your browser.");
    }
  };

  const speak = (text) => {
    if ('speechSynthesis' in window) {
      stopSpeaking();
      
      setIsSpeaking(true);
      const cleanText = removeMarkdown(text);
      const utterance = new SpeechSynthesisUtterance(cleanText);
      
      utterance.onend = () => {
        setIsSpeaking(false);
        speechRef.current = null;
      };
      
      utterance.onerror = () => {
        setIsSpeaking(false);
        speechRef.current = null;
      };
      
      speechRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    } else {
      alert("Text-to-speech is not supported in your browser.");
    }
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      speechRef.current = null;
    }
  };

  const handleCopy = (content, index) => {
    navigator.clipboard.writeText(content);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleDownloadPDF = async () => {
    const element = contentRef.current;
    const opt = {
      margin: 1,
      filename: 'analysis.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    html2pdf().set(opt).from(element).save();
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: title,
        text: messages.map(m => `${m.role}: ${m.content}`).join('\n\n'),
      });
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };
  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion.prompt);
    handleSubmit({ preventDefault: () => {} });
    
    // Rotate suggestions
    const newSuggestions = [...SUGGESTIONS]
      .sort(() => Math.random() - 0.5)
      .slice(0, 4);
    setActiveSuggestions(newSuggestions);
  };

  const generateAnalysis = async () => {
    setIsLoading(true);
    try {
      const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
      const model = genAI.getGenerativeModel({ model: "gemini-1.5-pro" });
      
      const prompt = `${initialPrompt}\n\nData:\n${JSON.stringify(data, null, 2)}`;
      const result = await model.generateContent([prompt]);
      const response = await result.response;
      const text = response.text();
      
      setMessages([{ role: "assistant", content: text }]);
    } catch (error) {
      console.error("Error generating analysis:", error);
      setMessages([{ 
        role: "assistant", 
        content: "I apologize, but I encountered an error while analyzing the data. Please try again." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
      const model = genAI.getGenerativeModel({ model: "gemini-1.5-pro" });
      
      const conversationContext = `Previous context: ${JSON.stringify(data)}\n\nConversation history:\n${messages.map(m => `${m.role}: ${m.content}`).join('\n')}\n\nUser: ${userMessage}`;
      
      const result = await model.generateContent([conversationContext]);
      const response = await result.response;
      const text = response.text();
      
      setMessages(prev => [...prev, { role: "assistant", content: text }]);
    } catch (error) {
      console.error("Error generating response:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I apologize, but I encountered an error. Please try again." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleDownloadPDF}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              title="Download PDF"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              onClick={handleShare}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              title="Share"
            >
              <Share2 className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

         {/* Messages section with improved action buttons */}
         <div className="flex-1 overflow-y-auto p-6 space-y-6" ref={contentRef}>
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div className="relative max-w-[85%]">
                <div
                  className={`rounded-2xl p-4 shadow-sm ${
                    message.role === "user"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-50 text-gray-900 border border-gray-100"
                  }`}
                >
                  <ReactMarkdown className="prose prose-sm max-w-none">
                    {message.content}
                  </ReactMarkdown>
                </div>
                {/* Action buttons */}
                <div className="flex items-center justify-end space-x-2 mt-2">
                  <button
                    onClick={() => handleCopy(message.content, index)}
                    className="p-2 rounded-lg bg-white shadow-md hover:bg-gray-50 transition-colors flex items-center text-sm"
                    title="Copy to clipboard"
                  >
                    {copiedIndex === index ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-green-500">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 text-gray-500 mr-1" />
                        <span className="text-gray-500">Copy</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => isSpeaking ? stopSpeaking() : speak(message.content)}
                    className={`p-2 rounded-lg shadow-md transition-colors flex items-center text-sm ${
                      isSpeaking 
                        ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                    title={isSpeaking ? "Stop speaking" : "Listen to text"}
                  >
                    {isSpeaking ? (
                      <>
                        <StopCircle className="h-4 w-4 mr-1" />
                        <span>Stop</span>
                      </>
                    ) : (
                      <>
                        <Volume2 className="h-4 w-4 mr-1" />
                        <span>Listen</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex flex-col items-center space-y-4 py-8">
              <Loader className="h-8 w-8 animate-spin text-blue-500" />
              <p className="text-gray-500 animate-pulse">{loadingMessage}</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions Section */}
        {messages.length > 0 && !isLoading && (
          <div className="px-6 pb-4">
            <div className="flex items-center space-x-2 mb-2">
              <Sparkles className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-gray-600">Suggested follow-ups</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {activeSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-full 
                    text-sm border border-gray-200 transition-colors flex items-center space-x-2"
                >
                  <span>{suggestion.icon}</span>
                  <span>{suggestion.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Form */}
        <form onSubmit={handleSubmit} className="p-6 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={startListening}
              className={`px-3 py-2 rounded-lg transition-colors ${
                isListening 
                  ? "bg-red-100 text-red-600" 
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {isListening ? (
                <StopCircle className="h-5 w-5" />
              ) : (
                <Mic className="h-5 w-5" />
              )}
            </button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={generateAnalysis}
              disabled={isLoading}
              className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GeminiAnalysis;